import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Modal,
  Row,
  Select,
  Spin,
  message,
} from "antd";
import { FileAddOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { postApi } from "../../redux/rest/restSlice";
import get from "lodash/get";
import {
  generatePaginationObj,
  getNextSort,
  TABLE_PAGINATION,
} from "../../utilities/helper";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  GENERATE_INVOICE_REPORT,
  INVOICE_REPORT_LIST,
} from "../../constants/actionTypes";
import { invoiceReportTable } from "../../constants/cabinet";
import Can from "../Can";
import DataTable from "../DataTable";
import { PERMISSIONS } from "../../constants/permissions";

const InvoiceReport = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState({
    data: null,
    cabinets: [],
    sort: null,
    pagination: {
      offset: 0,
      limit: 10,
      total: 0,
      current: 1,
    },
  });

  const [showModal, setShowModal] = useState(false);

  const fetchInvoiceReports = (
    page = 1,
    sort = null,
    limit = get(invoiceData, "pagination.limit")
  ) => {
    setLoading(true);

    const pageObj = generatePaginationObj(page, limit);

    dispatch(
      postApi({
        endpoint: INVOICE_REPORT_LIST,
        payload: {
          ...pageObj,
          ...(sort ? { sortBy: sort.sortBy, sortDir: sort.sortDir } : {}),
        },
      })
    )
      .then(unwrapResult)
      .then((result) => {
        setLoading(false);
        setInvoiceData({
          data: get(result, "data"),
          cabinets: get(result, "cabinets"),
          sort,
          pagination: {
            ...pageObj,
            total: get(result, "total"),
            current: page,
          },
        });
      })
      .catch(() => setLoading(false));
  };

  const onFinish = (values) => {
    setLoading(true);

    const payload = {
      ...values,
      start_date: new Date(values?.start_date?.$d).getTime(),
      end_date: new Date(values?.end_date?.$d).getTime(),
    };

    dispatch(
      postApi({
        endpoint: GENERATE_INVOICE_REPORT,
        payload,
      })
    )
      .then(unwrapResult)
      .then((result) => {
        setLoading(false);
        if (get(result, "success")) {
          message.success(get(result, "message"));
          setShowModal(false);
          fetchInvoiceReports(
            get(invoiceData, "pagination.current"),
            get(invoiceData, "sort")
          );
        }
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchInvoiceReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTableChange = (paginationObj, _filters, sorter, extra) => {
    if (get(extra, "action") === "sort") {
      // Created At / Updated At
      fetchInvoiceReports(1, getNextSort(sorter, get(invoiceData, "sort")));
      return;
    }

    // Page change, or page size change (back to page 1)
    const pageSize = get(paginationObj, "pageSize");
    const sizeChanged = pageSize !== get(invoiceData, "pagination.limit");

    fetchInvoiceReports(
      sizeChanged ? 1 : get(paginationObj, "current"),
      get(invoiceData, "sort"),
      pageSize
    );
  };

  const rows = get(invoiceData, "data");
  const firstLoad = loading && !(rows && rows.length);

  return (
    <Spin spinning={loading && !firstLoad}>
      <Can permissions={[PERMISSIONS.GENERATE_REPORTS]}>
        <div className="list-toolbar">
          <div className="list-toolbar__actions">
            <Button
              type="primary"
              className="default-primary-btn"
              icon={<FileAddOutlined />}
              onClick={() => {
                setShowModal(true);
              }}
            >
              Generate Report
            </Button>
          </div>
        </div>
      </Can>
      <DataTable
        skeleton={firstLoad}
        scrollX={900}
        rowKey="id"
        className="mt-20"
        dataSource={rows}
        columns={invoiceReportTable(
          get(invoiceData, "pagination.offset"),
          get(invoiceData, "sort")
        )}
        pagination={{
          ...TABLE_PAGINATION,
          pageSize: get(invoiceData, "pagination.limit"),
          total: get(invoiceData, "pagination.total"),
          current: get(invoiceData, "pagination.current"),
        }}
        onChange={onTableChange}
        emptyProps={{ noun: "invoice reports" }}
      />

      {showModal ? (
        <Modal
          width={520}
          title={"Generate Report"}
          open={showModal}
          onCancel={() => setShowModal(false)}
          footer={<></>}
        >
          <Form
            initialValues={{}}
            onFinish={onFinish}
            onFinishFailed={() => {}}
            className="mt-20"
          >
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item label={"Cabinet Name"} name={"cabinet_id"}>
                  <Select options={get(invoiceData, "cabinets")} />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={"Start Date"}
                  name={"start_date"}
                  rules={[
                    {
                      required: true,
                      message: "Please pick a start date.",
                    },
                  ]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={"End Date"}
                  name={"end_date"}
                  rules={[
                    {
                      required: true,
                      message: "Please pick an end date.",
                    },
                  ]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <div className="modal-actions">
              <Button
                className="default-secondary-btn"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                className="default-primary-btn"
                htmlType="submit"
              >
                Generate
              </Button>
            </div>
          </Form>
        </Modal>
      ) : null}
    </Spin>
  );
};

export default InvoiceReport;
